// Client/src/services/AppSocketService.ts
import { SocketClient, SocketAuthErrorEvent } from './SocketClient';

interface TaskUpdate {
  entityId: string;
  storeId: string;
  type: 'create' | 'update' | 'delete' | 'completed';
  task?: unknown;
  timestamp?: string;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
  data?: unknown;
}

// Zusätzliche Event-Interfaces für Dashboard
interface HmdUpdate {
  storeId: string;
  showWarning?: boolean;
  showTodayTask?: boolean;
  showSimpleIndicator?: boolean;
  hmdEntriesCount?: number;
  timestamp: string;
}

interface KatalogUpdate {
  id: string;
  isGlobal?: boolean;
  storeId?: string;
  timestamp: string;
}

interface StoreKatalogUpdate {
  storeId: string;
  timestamp: string;
}

interface StoreUpdate {
  storeId: string;
  type: string;
  timestamp: string;
}

interface RolliUpdate {
  storeId: string;
  timestamp: string;
}

interface RolliTimeEstimatesUpdate {
  storeId: string;
  computedAt: string;
  scope: 'store' | 'cross_market';
  lookbackDays: number;
  comparableWindowMinutes: number;
  minComparableSamples: number;
  estimates: Record<
    string,
    {
      rolliId: string;
      rolliName: string;
      defektur?: {
        medianMinutes: number;
        samplesUsed: number;
        samplesComparable: number;
        usedComparable: boolean;
      };
      regalpflege?: {
        medianMinutes: number;
        samplesUsed: number;
        samplesComparable: number;
        usedComparable: boolean;
      };
      totalMedianMinutes?: number;
    }
  >;
}

interface LayoutUpdate {
  storeId: string;
  timestamp: string;
}

interface LayoutHistorieUpdate {
  storeId: string;
  timestamp: string;
}

interface KampagneUpdate {
  timestamp: string;
}

export interface ShelfUpdate {
  type: 'create' | 'update' | 'delete';
  scope: 'action' | 'subscription';
  storeId?: string;
  entityId?: string;
  timestamp: string;
}

interface ReinigungUpdate {
  storeId: string;
  type?: string;
  entityId?: string;
  timestamp?: string;
}

interface ReconnectEvent {
  timestamp: string;
}

export interface SyncProgressEvent {
  type: 'kategorien_sync' | 'artikel_refresh' | 'mhd_import';
  status: 'started' | 'progress' | 'waiting' | 'done' | 'error';
  total: number;
  processed: number;
  currentItem: string | null;
  message: string;
  startedAt: number;
  elapsedSeconds: number;
  estimatedSecondsRemaining: number | null;
  percent: number;
}

export interface MaintenancePayload {
  active: boolean;
  phase: string;
  message?: string;
  scheduledSwitchAt?: string | null;
  downtimeMinutes?: number;
}

class AppSocketService {
  private socketClient: SocketClient;

  constructor() {
    this.socketClient = new SocketClient();
  }

  async connect(employeeId?: string): Promise<boolean> {
    return this.socketClient.connect({
      employeeId,
      connectionType: 'main'
    });
  }

  // Task-Events
  onTaskUpdate(callback: (update: TaskUpdate) => void): void {
    this.socketClient.on('task_update', callback);
  }

  offTaskUpdate(callback?: (update: TaskUpdate) => void): void {
    this.socketClient.off('task_update', callback);
  }

  // Notification-Events
  onNotification(callback: (notification: Notification) => void): void {
    this.socketClient.on('notification', callback);
  }

  offNotification(callback?: (notification: Notification) => void): void {
    this.socketClient.off('notification', callback);
  }

  // Dashboard-spezifische Events
  onHmdUpdate(callback: (update: HmdUpdate) => void): void {
    this.socketClient.on('hmd:update', callback);
  }

  offHmdUpdate(callback?: (update: HmdUpdate) => void): void {
    this.socketClient.off('hmd:update', callback);
  }

  onKatalogUpdate(callback: (update: KatalogUpdate) => void): void {
    this.socketClient.on('katalogeintrag:update', callback);
  }

  offKatalogUpdate(callback?: (update: KatalogUpdate) => void): void {
    this.socketClient.off('katalogeintrag:update', callback);
  }

  onStoreKatalogUpdate(callback: (update: StoreKatalogUpdate) => void): void {
    this.socketClient.on('storeKatalog:update', callback);
  }

  offStoreKatalogUpdate(callback?: (update: StoreKatalogUpdate) => void): void {
    this.socketClient.off('storeKatalog:update', callback);
  }

  onStoreUpdate(callback: (update: StoreUpdate) => void): void {
    this.socketClient.on('store:update', callback);
  }

  offStoreUpdate(callback?: (update: StoreUpdate) => void): void {
    this.socketClient.off('store:update', callback);
  }

  // Zweitplatzierungs-Aktionen / Markt-Abos
  onShelfUpdate(callback: (update: ShelfUpdate) => void): void {
    this.socketClient.on('shelf:update', callback);
  }

  offShelfUpdate(callback?: (update: ShelfUpdate) => void): void {
    this.socketClient.off('shelf:update', callback);
  }

  onReinigungUpdate(callback: (update: ReinigungUpdate) => void): void {
    this.socketClient.on('reinigung:update', callback);
  }

  offReinigungUpdate(callback?: (update: ReinigungUpdate) => void): void {
    this.socketClient.off('reinigung:update', callback);
  }

  onRolliUpdate(callback: (update: RolliUpdate) => void): void {
    this.socketClient.on('rolli:update', callback);
  }

  offRolliUpdate(callback?: (update: RolliUpdate) => void): void {
    this.socketClient.off('rolli:update', callback);
  }

  onRolliTimeEstimatesUpdate(callback: (update: RolliTimeEstimatesUpdate) => void): void {
    this.socketClient.on('rolli:time_estimates', callback);
  }

  offRolliTimeEstimatesUpdate(callback?: (update: RolliTimeEstimatesUpdate) => void): void {
    this.socketClient.off('rolli:time_estimates', callback);
  }

  onMaintenance(callback: (payload: MaintenancePayload) => void): void {
    this.socketClient.on('maintenance', callback);
  }

  offMaintenance(callback?: (payload: MaintenancePayload) => void): void {
    this.socketClient.off('maintenance', callback);
  }

  onLayoutUpdate(callback: (update: LayoutUpdate) => void): void {
    this.socketClient.on('layout:update', callback);
  }

  offLayoutUpdate(callback?: (update: LayoutUpdate) => void): void {
    this.socketClient.off('layout:update', callback);
  }

  onLayoutHistorieUpdate(callback: (update: LayoutHistorieUpdate) => void): void {
    this.socketClient.on('layout:historie', callback);
  }

  offLayoutHistorieUpdate(callback?: (update: LayoutHistorieUpdate) => void): void {
    this.socketClient.off('layout:historie', callback);
  }

  onKampagneUpdate(callback: (update: KampagneUpdate) => void): void {
    this.socketClient.on('kampagne:update', callback);
  }

  offKampagneUpdate(callback?: (update: KampagneUpdate) => void): void {
    this.socketClient.off('kampagne:update', callback);
  }

  onReconnect(callback: (update: ReconnectEvent) => void): void {
    this.socketClient.on('reconnect', callback);
  }

  offReconnect(callback?: (update: ReconnectEvent) => void): void {
    this.socketClient.off('reconnect', callback);
  }

  onAuthError(callback: (event: SocketAuthErrorEvent) => void): void {
    this.socketClient.on('auth_error', callback);
  }

  offAuthError(callback?: (event: SocketAuthErrorEvent) => void): void {
    this.socketClient.off('auth_error', callback);
  }

  // Allgemeine Events
  onSocketInfo(callback: (info: unknown) => void): void {
    this.socketClient.on('socket_info', callback);
  }

  onSyncProgress(callback: (event: SyncProgressEvent) => void): void {
    this.socketClient.on('sync:progress', callback);
  }

  offSyncProgress(callback?: (event: SyncProgressEvent) => void): void {
    this.socketClient.off('sync:progress', callback);
  }

  // Nachrichten senden
  sendTaskCompleted(taskId: string, data?: unknown): void {
    this.socketClient.send('task_completed', { taskId, data });
  }

  sendPing(): void {
    this.socketClient.send('ping', {});
  }

  // Status
  isConnected(): boolean {
    return this.socketClient.isConnected &&
           (this.socketClient.currentConnectionType === 'main' || this.socketClient.currentConnectionType === 'admin');
  }

  getConnectionInfo(): unknown {
    return this.socketClient.connectionInfo;
  }

  disconnect(): void {
    this.socketClient.disconnect();
  }
}

// Singleton-Instanz
const appSocketService = new AppSocketService();
export default appSocketService;
export { AppSocketService };
